import { useNavigate } from "react-router-dom"
import { useCallback, useState } from "react"
import {
    Button,
    Typography,
    useAlert,
    Loading,
    Overlay,
    useConfirm
} from "matsuri-ui"
import { css } from "@emotion/react"
import { Capacitor } from "@capacitor/core"
import { FilePicker } from "@capawesome/capacitor-file-picker"
import { Filesystem } from "@capacitor/filesystem"
import { Toast } from "@capacitor/toast"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import dayjs from "dayjs"
import { useApprovalRequestIdFromParams } from "./useApprovalRequestIdFromParams"
import { FooterButtons } from "./FooterButton"
import { useApprovalRequestDetailViewProps } from "./useApprovalRequestDetailViewProps"
import { useConfirmRejectDialog } from "./ConfirmRejectDialog"
import { useApprovalRequestMutation } from "@/hooks/useApprovalRequest"
import { ApprovalRequestPageHeader } from "@/parts/ApprovalRequestPageHeader"
import { sleep } from "@/utils/sleep"
import { useAlreadyTakenDialog } from "@/parts/ApprovalRequest/useAlreadyTakenDialog"
import { showJpYen } from "@/utils/showJpYen"
import { theme } from "@/theme"
import { LoadingMessage } from "@/components/LoadingMessage/LoadingMessage"
import { blobToBase64, downloadPdfFile } from "@/utils/file"
import { FilePath } from "@/plugins/filepath"
import { useCleaningExtraInfo } from "@/hooks/useCleaning"
import { openUrlInNewTab, sumycaListingUrl } from "@/utils/urls"
import { useTranslation } from "@/hooks/useTranslation"

const ApprovalRequestDetailPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { throwAlert } = useAlert()
    const { token } = useAuth()
    const { approvalRequestId } = useApprovalRequestIdFromParams()
    const [isLoading, setIsLoading] = useState(false)
    const [isButtonLoading, setIsButtonLoading] = useState(false)

    const {
        openAlreadyTakenDialogWhenApproving,
        openAlreadyTakenDialogWhenRejecting
    } = useAlreadyTakenDialog()

    const viewProps = useApprovalRequestDetailViewProps({
        approvalRequestId
    })

    const { data: cleaningExtraInfo } = useCleaningExtraInfo(token, {
        approvalRequestId: approvalRequestId
    })

    const {
        requestApproveApprovalRequests,
        requestRejectApprovalRequests,
        createApprovalRequestsPdf
    } = useApprovalRequestMutation()

    const handleApproveButtonClick = useCallback(async () => {
        setIsButtonLoading(true)
        try {
            const { error } = await requestApproveApprovalRequests({
                requestIds: [approvalRequestId]
            })

            if (error && error.parse?.()?.errorType === "NotOwnedByCleaner") {
                openAlreadyTakenDialogWhenApproving()
                return
            }

            throwAlert(error, {
                successMessage: t("承認リクエストの承認に成功しました"),
                errorMessage: t("承認リクエストの承認に失敗しました")
            })
            if (!error) {
                await sleep(800)
                navigate("/approval_requests")
            }
        } finally {
            setIsButtonLoading(false)
        }
    }, [
        requestApproveApprovalRequests,
        approvalRequestId,
        throwAlert,
        openAlreadyTakenDialogWhenApproving,
        navigate
    ])

    const onConfirmRejectButtonClick = useCallback(
        async (closeModal: () => void) => {
            const { error } = await requestRejectApprovalRequests({
                requestIds: [approvalRequestId]
            })

            if (error && error.parse?.()?.errorType === "NotOwnedByCleaner") {
                openAlreadyTakenDialogWhenRejecting()
                return
            }

            throwAlert(error, {
                successMessage: t("承認リクエストの却下に成功しました"),
                errorMessage: t("承認リクエストの却下に失敗しました")
            })

            if (!error) {
                await sleep(800)
                closeModal()
                navigate("/approval_requests")
            }
        },
        [
            requestRejectApprovalRequests,
            approvalRequestId,
            throwAlert,
            openAlreadyTakenDialogWhenRejecting,
            navigate
        ]
    )

    const { openRejectModal, closeRejectModal } = useConfirmRejectDialog({
        onConfirmReject: async () =>
            await onConfirmRejectButtonClick(closeRejectModal)
    })

    const labelAndValueList: { label: string; value: string }[] = [
        {
            label: t("発注事業者の名称"),
            value: viewProps.orderingCompanyName
        },
        {
            label: t("受注事業者の名称"),
            value: viewProps.receivingCompanyName
        },
        {
            label: t("業務委託をした日"),
            value: viewProps.executionDate
        },
        {
            label: t("給付・役務の内容"),
            value: viewProps.tourName
        },
        {
            label: t("給付・役務提供の期日"),
            value: viewProps.cleaningDate
        },
        {
            label: t("給付・役務提供の場所"),
            value: viewProps.placementName
        },

        {
            label: t("報酬・下請代金の額"),
            value: showJpYen(viewProps.unitPrice ?? 0)
        },

        {
            label: t("報酬・下請代金の支払期日"),
            value: viewProps.paymentDueDate
        },
        {
            label: t("検査完了日"),
            value: viewProps.inspectionDate
        }
    ].filter((v): v is { label: string; value: string } => Boolean(v))

    const confirm = useConfirm()

    const generatePDF = async () => {
        let message = ""
        try {
            setIsLoading(true)
            const fileName = "order.pdf"

            // 注文書PDFファイルを生成
            const response = await createApprovalRequestsPdf(approvalRequestId)
            const blob = await response.blob()

            if (Capacitor.isNativePlatform()) {
                let buttonName = t("開く")
                let imageName = "instruction_document_download_ios.png"

                if (Capacitor.getPlatform() === "android") {
                    buttonName = t("このフォルダを使用")
                    imageName = "instruction_document_download_android.png"
                }

                if (
                    !(await confirm({
                        title: t("ダウンロード方法"),
                        message: (
                            <>
                                <div>
                                    {t(
                                        `次の画面にファイルを保存するフォルダを選択してから、「${buttonName}」を押してください`
                                    )}
                                </div>
                                <div
                                    css={css`
                                        display: flex;
                                        justify-content: center;
                                        margin-top: 8px;
                                    `}
                                >
                                    <img
                                        src={`/assets/${imageName}`}
                                        alt="download instructions"
                                        style={{ height: "50vh" }}
                                    />
                                </div>
                            </>
                        ),
                        confirmButtonText: t("続ける")
                    }))
                ) {
                    return
                }

                const data = await blobToBase64(blob)

                // 保存先のフォルダを選択
                const { path: selectedPath } = await FilePicker.pickDirectory()
                const { path } = await FilePath.format({
                    contentUri: selectedPath,
                    fileName: fileName
                })

                // 指定したフォルダにファイルを保存
                await Filesystem.writeFile({ path: path, data: data as string })
            } else {
                // ファイルをダウンロード
                await downloadPdfFile(blob, fileName)
            }
        } catch (error) {
            console.error(error)
            message = t("注文書がダウンロードできませんでした")
        } finally {
            setIsLoading(false)
            message = message || t("注文書をダウンロードしました")
        }

        // アプリの場合はToastが表示される
        await Toast.show({ text: message, position: "center" })
    }

    return (
        <div
            css={css`
                position: relative;
                width: 100%;
                height: 100%;
            `}
        >
            <Overlay fullScreen open={isLoading}>
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 16px;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                    `}
                >
                    <Typography variant="h1" as="div">
                        <Loading />
                    </Typography>
                    <LoadingMessage />
                </div>
            </Overlay>
            <ApprovalRequestPageHeader
                onPrev={() => navigate("/approval_requests")}
            />
            <div
                css={css`
                    display: grid;
                    gap: 16px;
                    padding: 16px;
                    overflow: scroll;
                    white-space: pre-line;
                `}
            >
                {labelAndValueList.map(({ label, value }) => (
                    <div key={label}>
                        <Typography
                            variant="h4"
                            as="h4"
                            css={css`
                                margin-bottom: 8px;
                            `}
                        >
                            {label}
                        </Typography>
                        <Typography>{value}</Typography>
                    </div>
                ))}
                {(viewProps.deadlineAt || viewProps.hasCheckinOnDate) && (
                    <div>
                        <Typography
                            variant="h4"
                            as="h4"
                            css={css`
                                margin-bottom: 8px;
                                color: #fd544b;
                            `}
                        >
                            {t("清掃期限")}
                        </Typography>
                        <Typography
                            css={css`
                                font-weight: 700;
                                color: #fd544b;
                            `}
                        >
                            {viewProps.deadlineAt
                                ? `〜${dayjs.unix(viewProps.deadlineAt).format("HH:mm")}`
                                : "〜16:00"}
                        </Typography>
                    </div>
                )}
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                    `}
                >
                    <Typography align="center" bolder>
                        {t("清掃の追加情報")}
                    </Typography>
                    <Typography>
                        {t("物件の詳細情報（Sumycaリンク）")}
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        readOnly={!cleaningExtraInfo?.sumycaListingId}
                        onClick={() => {
                            if (cleaningExtraInfo?.sumycaListingId) {
                                openUrlInNewTab(
                                    sumycaListingUrl(
                                        cleaningExtraInfo.sumycaListingId
                                    )
                                )
                            }
                        }}
                        endIcon={<OpenInNewIcon />}
                    >
                        {t("Sumycaで確認する")}
                    </Button>
                    <Typography>{t("※外部リンクで開きます")}</Typography>
                </div>
                <div
                    css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 8px;
                        margin-top: 16px;
                    `}
                >
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={generatePDF}
                        css={css`
                            flex: 1;
                        `}
                    >
                        {t("注文書をダウンロード")}
                    </Button>
                    <Typography color="secondary" bolder>
                        {t("重要な資料ですので必ず保管をお願いします")}
                    </Typography>
                </div>
            </div>

            <div
                css={css`
                    padding: 16px;
                    background-color: ${theme.palette.gray[100]};
                `}
            >
                <Typography variant="h4" as="span">
                    {t("注意事項")}
                </Typography>
                <Typography variant="body">
                    {t(
                        "※研修業務をご依頼させて頂く場合にも、清掃業務に関するご依頼の通知がシステムの仕様上送られます。研修業務の実施につきまして、ご承諾頂ける場合には大変恐れ入りますが、併せてご承認頂きますようにお願いいたします。"
                    )}
                </Typography>
            </div>

            <FooterButtons
                onApproveButtonClick={handleApproveButtonClick}
                onRejectButtonClick={openRejectModal}
                isLoading={isButtonLoading}
            />
        </div>
    )
}

export default ApprovalRequestDetailPage
